import type { ReactNode } from "react";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useSEO } from "../hooks/useSEO";

/* ============================================================
   Reusable legal-document layout and typography
   ============================================================ */

const LegalDocumentTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="legal-document__section-title">{children}</h2>
);

const LegalDocumentDivider = () => (
  <div className="legal-document__divider" aria-hidden="true" />
);

const H2 = ({ children }: { children: ReactNode }) => (
  <h2 className="legal-document__heading">{children}</h2>
);

const P = ({ children }: { children: ReactNode }) => (
  <p className="legal-document__paragraph">{children}</p>
);

const Sub = ({ children }: { children: ReactNode }) => (
  <p className="legal-document__subheading">{children}</p>
);

const UL = ({ items }: { items: ReactNode[] }) => (
  <ul className="legal-document__list">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

/* ============================================================
                            PAGE
   ============================================================ */

export default function TermsandCondition() {
  useSEO({
    title: "Terms & Conditions — Vastavik",
    description:
      "Read the Terms & Conditions governing the use of Vastavik's media authenticity and verification services.",
    path: "/termsandcondition",
  });
  return (
    <div className="min-h-screen bg-white flex flex-col text-black">
      <Header />

      <main className="legal-page">
        <div className="legal-page__container">
          <header className="legal-document__header">
            <h1 className="legal-document__title">Privacy Policy</h1>

            <p className="legal-document__meta">
              Last Updated:{" "}
              <time className="legal-document__date">04/02/2026</time>
            </p>
          </header>

          <div
            className="legal-document__panel"
            tabIndex={0}
            aria-label="Privacy Policy, Terms of Service, and End-User License Agreement"
          >
            <div className="legal-document__content">
          <P>
            ADROITX LIMITED (&ldquo;Vastavik,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your
            information when you use the Vastavik application, website, and related
            services (collectively, the &ldquo;Services&rdquo;).
          </P>
          <P>
            By accessing or using the Services, you consent to the practices described
            in this Policy.
          </P>

          <H2>1. Information We Collect</H2>
          <P>We collect the following categories of information:</P>

          <Sub>1.1. Information You Provide</Sub>
          <UL
            items={[
              "Account Information: Name, email address, phone number, username, and password.",
              "Uploaded Media: Photos, videos, audio, or other files you submit for authentication.",
              "Verification Metadata: Hashes, timestamps, and associated NFT data generated during blockchain processing.",
              "Payment Information: Billing details processed via secure third-party payment providers (we do not store full card details).",
            ]}
          />

          <Sub>1.2. Information Automatically Collected</Sub>
          <UL
            items={[
              "Device Information: Device model, operating system, IP address, browser type.",
              "Usage Data: Interactions within the app, access times, clicks, and feature usage.",
              "Cookies & Tracking: Cookies or similar technologies used to improve functionality and analytics.",
            ]}
          />

          <Sub>1.3. Blockchain & NFT Data</Sub>
          <P>When you use blockchain features, we may generate and store:</P>
          <UL
            items={[
              "Cryptographic hashes of your media,",
              "NFT identifiers,",
              "Immutable ledger entries through Hyperledger Fabric.",
            ]}
          />
          <P>
            <span className="text-[#d33]">⚠ Note:</span> Blockchain entries are permanent
            and cannot be altered or deleted once recorded.
          </P>

          <H2>2. How We Use Your Information</H2>
          <P>We use your information to:</P>
          <UL
            items={[
              "Provide, operate, and maintain the Services,",
              "Register and authenticate your media on blockchain,",
              "Generate private or public NFTs based on your selections,",
              "Improve user experience and develop new features,",
              "Process transactions and provide receipts,",
              "Communicate with you regarding updates, support, or security notices,",
              "Prevent fraud, abuse, and unauthorized activity,",
              "Comply with legal requirements.",
            ]}
          />
          <P>We do not sell your personal information.</P>

          <H2>3. How Your Information Is Shared</H2>
          <P>We may share information in the following scenarios:</P>

          <Sub>3.1. With Your Consent</Sub>
          <P>
            If you choose to create public NFTs, verification data may be accessible to
            the public.
          </P>

          <Sub>3.2. With Service Providers</Sub>
          <P>We use trusted third-party providers for:</P>
          <UL
            items={[
              "Cloud storage,",
              "Payment processing,",
              "Analytics,",
              "Customer support tools.",
            ]}
          />
          <P>These providers are contractually obligated to protect your data.</P>

          <Sub>3.3. Blockchain Networks</Sub>
          <P>Hyperledger Fabric blockchain records are:</P>
          <UL
            items={[
              "Public or private depending on your choice,",
              "Immutable and not erasable after registration.",
            ]}
          />

          <Sub>3.4. Legal Requirements</Sub>
          <P>We may disclose your information:</P>
          <UL
            items={[
              "To comply with applicable laws or legal processes,",
              "To enforce our Terms of Service,",
              "To protect the rights, safety, or property of Vastavik or others.",
            ]}
          />

          <H2>4. Data Retention</H2>
          <P>We retain:</P>
          <UL
            items={[
              "Account data until you request deletion,",
              "Uploaded media until you remove it or request deletion,",
              "Blockchain data permanently (cannot be changed or removed),",
              "Transaction records for as long as required by financial regulations.",
            ]}
          />

          <H2>5. Your Rights</H2>
          <P>Depending on your location, you may have the right to:</P>
          <UL
            items={[
              "Access your personal data,",
              "Request correction of inaccurate information,",
              "Request deletion of your account or media (excluding blockchain records),",
              "Object to or restrict certain types of processing,",
              "Download or transfer your data (\u201cdata portability\u201d),",
              "Opt-out of marketing communications.",
            ]}
          />
          <P>To submit a request, contact us using the information below.</P>

          <H2>6. Cookies & Tracking Technologies</H2>
          <P>We use cookies to:</P>
          <UL
            items={[
              "Improve performance,",
              "Remember user preferences,",
              "Understand usage trends.",
            ]}
          />
          <P>
            You may disable cookies in your browser settings, though some features may
            not function properly.
          </P>

          <H2>7. Data Security</H2>
          <P>We implement reasonable security measures, including:</P>
          <UL items={["Encryption,", "Access controls,", "Secure hashing,", "Network safeguards."]} />
          <P>
            However, no digital platform is 100% secure, and we cannot guarantee
            absolute protection.
          </P>

          <H2>8. Children&rsquo;s Privacy</H2>
          <P>
            The Services are not intended for individuals under 18 years of age. We do
            not knowingly collect information from minors. If you believe a minor has
            provided information to us, please contact us for immediate removal
            (excluding immutable blockchain data).
          </P>

          <H2>9. International Data Transfers</H2>
          <P>
            Your data may be stored or processed in countries other than your own. By
            using the Services, you consent to the transfer of information to such
            locations where data protection laws may differ.
          </P>

          <H2>10. Changes to This Privacy Policy</H2>
          <P>
            We may update this Policy periodically. Updates take effect once posted.
            Your continued use of the Services after changes indicates acceptance.
          </P>

          <H2>11. Contact Us</H2>
          <P>For privacy questions, data access requests, or account deletion:</P>
          <UL
            items={[
              <>Email: <a href="mailto:privacy@vastavik.org" className="underline underline-offset-4 hover:text-[#d33] transition">privacy@vastavik.org</a></>,
              <>Website: <a href="https://vastavik.org" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-[#d33] transition">vastavik.org</a></>,
            ]}
          />

              <LegalDocumentDivider />
              <LegalDocumentTitle>Terms of Service (ToS)</LegalDocumentTitle>

          <P>
            Welcome to Vastavik. These Terms of Service (&ldquo;Terms&rdquo;) govern your
            access to and use of the Vastavik mobile application, website, services, and
            related technologies (collectively, &ldquo;Services&rdquo;). By accessing or
            using the Services, you agree to these Terms. If you do not agree, do not
            use the Services.
          </P>

          <H2>1. Acceptance of Terms</H2>
          <P>By using the Services, you confirm that:</P>
          <UL
            items={[
              "You are at least 18 years old,",
              "You have the legal capacity to enter a binding agreement,",
              "You accept and will comply with these Terms and our Privacy Policy.",
            ]}
          />
          <P>
            If you are using the Services on behalf of an organization, you represent
            that you are authorized to bind the entity to these Terms.
          </P>

          <H2>2. Description of Services</H2>
          <P>Vastavik provides tools for:</P>
          <UL
            items={[
              "Authenticating user-generated media using blockchain and NFT-based technology,",
              "Recording immutable metadata for verification,",
              "Allowing users to assert authenticity of media in public and private domains.",
            ]}
          />
          <P>
            We may modify, update, or discontinue features at any time without
            liability.
          </P>

          <H2>3. User Accounts</H2>
          <P>To use certain features, you must create an account. You agree to:</P>
          <UL
            items={[
              "Provide truthful and accurate information,",
              "Keep your login details secure,",
              "Notify us immediately of unauthorized access.",
            ]}
          />
          <P>You are responsible for all activity conducted under your account.</P>

          <H2>4. User Content</H2>
          <P>
            &ldquo;User Content&rdquo; includes any media, data, images, videos, audio
            files, or metadata you upload or process through the Services.
          </P>
          <P>By using the Services, you confirm that:</P>
          <UL
            items={[
              "You own or have the legal right to upload the media,",
              "Your content does not violate copyrights, privacy rights, or applicable laws,",
              "You understand that blockchain entries created for authentication may be permanent.",
            ]}
          />
          <P>
            We do not claim ownership of your media, but you grant us a license to
            process it for:
          </P>
          <UL
            items={[
              "Hash generation,",
              "NFT creation,",
              "Blockchain registration,",
              "Service functionality and maintenance.",
            ]}
          />

          <H2>5. Blockchain Records & NFTs</H2>
          <P>Vastavik uses Hyperledger Fabric and NFT-based identifiers.</P>
          <P>By using these features, you acknowledge:</P>
          <UL
            items={[
              "Blockchain entries are immutable and cannot be deleted, altered, or erased,",
              "You may request deletion of platform-stored files, but the blockchain record itself remains,",
              "Private NFTs are default; public NFTs are optional and may require additional fees,",
              "You are responsible for double-checking your media before submission.",
            ]}
          />

          <H2>6. Payments & Fees</H2>
          <P>Some features require payment. You agree that:</P>
          <UL
            items={[
              "All fees are non-refundable unless required by law,",
              "Prices may change with reasonable notice,",
              "Failure to pay may result in limited access or termination of features.",
            ]}
          />

          <H2>7. Acceptable Use</H2>
          <P>You agree NOT to use Vastavik to:</P>
          <UL
            items={[
              "Upload illegal, harmful, defamatory, or misleading content,",
              "Create, enhance, or validate deepfakes or fraudulent media,",
              "Impersonate individuals or institutions,",
              "Circumvent security features or tamper with blockchain data,",
              "Interfere with service integrity or other users.",
            ]}
          />
          <P>We may suspend or terminate accounts that violate these Terms.</P>

          <H2>8. Intellectual Property</H2>
          <P>
            All intellectual property related to the Services—including branding,
            software, algorithms, designs, and documentation—is owned by Vastavik
            Technologies.
          </P>
          <P>Your use of the Services does not transfer any ownership rights.</P>

          <H2>9. Privacy</H2>
          <P>
            Your use of the Services is governed by the Vastavik Privacy Policy, which
            describes:
          </P>
          <UL
            items={[
              "What data we collect,",
              "How we use it,",
              "How we store it,",
              "Your rights regarding personal data.",
            ]}
          />
          <P>You agree to review the Privacy Policy regularly.</P>

          <H2>10. Third-Party Services</H2>
          <P>
            The Services may integrate with third-party platforms or tools (e.g.,
            wallets, cloud storage providers). We are not responsible for:
          </P>
          <UL
            items={[
              "Failures of third-party systems,",
              "Data loss due to external integrations,",
              "Changes made by third-party service providers.",
            ]}
          />
          <P>Your use of third-party tools is subject to their respective terms.</P>

          <H2>11. Service Availability</H2>
          <P>We strive to maintain reliable access but do not guarantee:</P>
          <UL
            items={[
              "Uninterrupted operation,",
              "Error-free performance,",
              "Complete security against all threats.",
            ]}
          />
          <P>We may temporarily suspend the Services for maintenance or updates.</P>

          <H2>12. Disclaimer of Warranties</H2>
          <P>
            The Services are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo;
            We make no warranties, express or implied, including:
          </P>
          <UL
            items={[
              "Accuracy or reliability of blockchain data,",
              "100% security or immutability,",
              "That Services will meet your expectations.",
            ]}
          />

          <H2>13. Limitation of Liability</H2>
          <P>To the fullest extent permitted by law, Vastavik is not liable for:</P>
          <UL
            items={[
              "Loss of profits, revenue, or data,",
              "Misuse of your media by third parties,",
              "Damages related to blockchain immutability,",
              "Any indirect, incidental, or consequential damages.",
            ]}
          />
          <P>
            Our total liability will not exceed the amount you paid in the 12 months
            before the claim.
          </P>

          <H2>14. Termination</H2>
          <P>We may suspend or terminate your account if you:</P>
          <UL
            items={[
              "Violate these Terms,",
              "Misuse Services,",
              "Provide false information,",
              "Engage in harmful or fraudulent activity.",
            ]}
          />
          <P>Upon termination:</P>
          <UL
            items={[
              "Your license to use Vastavik ends,",
              "Blockchain entries remain intact and cannot be removed.",
            ]}
          />

          <H2>15. Governing Law</H2>
          <P>
            These Terms are governed by the laws of the jurisdiction in which Vastavik
            Technologies is incorporated, regardless of conflict-of-law provisions.
          </P>

          <H2>16. Changes to Terms</H2>
          <P>
            We may update these Terms at any time. Continued use after changes means you
            accept the updated Terms.
          </P>

          <H2>17. Contact</H2>
          <P>For support or legal inquiries:</P>
          <UL
            items={[
              <>Email: <a href="mailto:privacy@vastavik.org" className="underline underline-offset-4 hover:text-[#d33] transition">privacy@vastavik.org</a></>,
              <>Website: <a href="https://vastavik.org" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-[#d33] transition">vastavik.org</a></>,
            ]}
          />

              <LegalDocumentDivider />
              <LegalDocumentTitle>
                End-User License Agreement (EULA)
              </LegalDocumentTitle>

          <P>
            This End-User License Agreement (&ldquo;Agreement&rdquo;) is a legal
            contract between you (&ldquo;User,&rdquo; &ldquo;You,&rdquo; or
            &ldquo;Your&rdquo;) and ADROITX LIMITED (&ldquo;Company,&rdquo;
            &ldquo;We,&rdquo; &ldquo;Us,&rdquo; or &ldquo;Our&rdquo;) governing your use
            of the Vastavik mobile application, website, platform, and related services
            (collectively, the &ldquo;Application&rdquo;).
          </P>
          <P>
            By downloading, installing, accessing, or using the Application, you
            acknowledge that you have read, understood, and agree to be bound by this
            Agreement. If you do not agree, you must not access or use the Application.
          </P>

          <H2>1. License Grant</H2>
          <P>
            We grant you a limited, non-exclusive, non-transferable, revocable license
            to:
          </P>
          <UL
            items={[
              "Access and use the Application for personal or authorized professional purposes.",
              "Register and authenticate your media using the Application's blockchain and NFT-based verification services.",
            ]}
          />
          <P>This license does not permit you to:</P>
          <UL
            items={[
              "Modify, reverse engineer, or decompile any part of the Application.",
              "Use the Application for unlawful or harmful purposes.",
              "Resell, sublicense, or commercially exploit the Application without written consent.",
            ]}
          />

          <H2>2. Eligibility</H2>
          <P>You must:</P>
          <UL
            items={[
              "Be at least 18 years old, and",
              "Have the legal capacity to enter into a binding agreement.",
            ]}
          />
          <P>
            If you use the Application on behalf of an organization, you affirm that you
            have the authority to bind that entity.
          </P>

          <H2>3. User Responsibilities</H2>
          <P>You agree to:</P>
          <UL
            items={[
              "Provide accurate information during registration.",
              "Use the Application only for lawful, ethical purposes.",
              "Ensure that the media you upload is your own or that you have full rights to process it.",
            ]}
          />
          <P>Avoid uploading content that violates privacy, copyrights, or applicable laws.</P>
          <P>
            You understand that Vastavik does not verify ownership of your media and
            relies on your representation of rights and permissions.
          </P>

          <H2>4. Blockchain Registration & NFTs</H2>
          <P>
            The Application uses Hyperledger Fabric blockchain and NFT-based identifiers
            to register media authenticity.
          </P>
          <P>By using this feature, you acknowledge that:</P>
          <UL
            items={[
              "Media registered on blockchain becomes part of an immutable ledger.",
              "You cannot modify or delete blockchain records except through a formal request process, if permitted.",
              "Private NFTs are the default setting; public NFTs may be created at an additional cost.",
              "NFT entries may remain permanently visible depending on the blockchain architecture.",
            ]}
          />

          <H2>5. Data Usage & Privacy</H2>
          <P>Your use of the Application is also governed by our Privacy Policy.</P>
          <P>You authorize Vastavik to:</P>
          <UL
            items={[
              "Store metadata required for authentication.",
              "Process your media for cryptographic hashing and blockchain operations.",
              "Retain records necessary for legal, operational, and dispute-resolution purposes.",
            ]}
          />
          <P>We do not sell your personal data.</P>

          <H2>6. Prohibited Use</H2>
          <P>You may not use the Application to:</P>
          <UL
            items={[
              "Create, support, or distribute harmful, misleading, or malicious content.",
              "Impersonate another person or entity.",
              "Upload illegal, defamatory, or infringing media.",
              "Attempt to bypass security measures or tamper with blockchain entries.",
            ]}
          />
          <P>Violation may result in immediate termination of access without refund.</P>

          <H2>7. Payment & Fees</H2>
          <P>
            Certain features—such as public NFT registration—may require additional fees.
            All payments are:
          </P>
          <UL
            items={[
              "Non-refundable unless required by law.",
              "Subject to pricing changes with reasonable prior notice.",
            ]}
          />

          <H2>8. Intellectual Property</H2>
          <P>
            All intellectual property related to the Application—including software,
            branding, design, algorithms, and documentation—is owned by Vastavik
            Technologies.
          </P>
          <P>This Agreement does not transfer any intellectual property rights to you.</P>

          <H2>9. Disclaimers</H2>
          <P>The Application is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo;</P>
          <P>We do not guarantee that:</P>
          <UL
            items={[
              "The Application will be error-free, uninterrupted, or secure.",
              "Blockchain records are infallible or immune to all cyberattacks.",
              "Third-party integrations (e.g., wallets, platforms) will remain functional.",
            ]}
          />
          <P>We are not responsible for:</P>
          <UL
            items={[
              "Misuse of your media by third parties.",
              "Incorrect classification of your media by the public.",
            ]}
          />

          <H2>10. Limitation of Liability</H2>
          <P>
            To the maximum extent permitted by law, Vastavik Technologies is not liable
            for:
          </P>
          <UL
            items={[
              "Loss of data, profits, revenue, or goodwill.",
              "Any damages arising from unauthorized use of your media.",
              "Any blockchain-related immutability or permanence of stored data.",
            ]}
          />
          <P>
            Our total liability will not exceed the amount you paid (if any) in the 12
            months prior to the claim.
          </P>

          <H2>11. Termination</H2>
          <P>We may suspend or terminate your access to the Application if:</P>
          <UL
            items={[
              "You violate this Agreement,",
              "You misuse the platform, or",
              "We discontinue the service.",
            ]}
          />
          <P>Upon termination:</P>
          <UL
            items={[
              "Your license ends immediately.",
              "Blockchain entries remain immutable and cannot be erased.",
            ]}
          />

          <H2>12. Governing Law</H2>
          <P>
            This Agreement is governed by the laws of the jurisdiction where Vastavik
            Technologies is legally registered, without regard to conflict-of-law
            principles.
          </P>

          <H2>13. Changes to This Agreement</H2>
          <P>
            We may update this EULA from time to time. Continued use of the Application
            after updates constitutes acceptance of the revised Agreement.
          </P>

          <H2>14. Contact Information</H2>
          <P>For questions, concerns, or deletion requests, contact:</P>
          <UL
            items={[
              <>Email: <a href="mailto:privacy@vastavik.org" className="underline underline-offset-4 hover:text-[#d33] transition">privacy@vastavik.org</a></>,
              <>Website: <a href="https://vastavik.org" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-[#d33] transition">vastavik.org</a></>,
            ]}
          />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}